import { RabenConsentSnapshotSchema } from 'src/Ghg/interfaces';
// eslint-disable-next-line import/no-extraneous-dependencies
import PoppinsRegularUrl from 'src/views/Report/Pdf/fonts/Poppins-Regular.ttf';
import { AnchorSpec } from './pdfAnchors';
import { stampPdf, downloadPdfBytes } from './pdfStamp';
import {
  formatStatementDate,
  resolveStatementLanguage,
  StatementLanguage,
} from './statementFormat';

// C16 (specs/001-raben-country-masteradmin/mass-specs/specs/20260826-decl-
// commitment-tab-pdf.md, Goal 3): stamps the four fillable fields onto page
// 3 of the REAL source PDF (reference/docs/sbti-list-zobowiazania-
// przewoznika-PL.pdf / sbti-supplier-commitment-letter-EN.pdf, served as
// public/assets/raben/supplier-commitment-letter-{pl,en}.pdf) with pdf-lib,
// instead of recreating the page with react-pdf. Two problems forced that
// switch: react-pdf's built-in Helvetica has no Latin-Extended-A glyphs (a
// real generated file read "Zapoznalem/am si z peBn tre[ci" -- "ł"/"ą"/"ę"
// destroyed), and a hand-recreated page never quite matched the source's
// layout (justification, spacing, the "YOUR PARTNER IN LOGISTICS" footer).
// Stamping the real PDF fixes both by construction: the static text is
// whatever the source document already has, untouched: only the four
// drawn values need a font, and pdfStamp.ts embeds Poppins for those (the
// same .ttf files frontend/src/utils/fonts.tsx registers for the react-pdf
// reports -- see that file for why Poppins was already proven to render
// Polish glyphs, via the certificate at Ghg/components/OrganizationSidebar/
// DownloadCertificateItem/PdfDocument).
//
// Every displayed value comes from the immutable snapshot
// (RabenConsentSnapshotSchema), never from the live Organization -- that is
// what makes EC-4 (regeneration survives a later org rename) hold on the
// frontend too, matching the backend's CommitmentAcceptance model.

export type SupplierCommitmentPdfLanguage = StatementLanguage;

// BR-3 (amended 2026-08-27: the official German documents arrived): the
// source document exists in PL/EN/DE -- the remaining UI locales (cs/nl)
// fall back to EN, same idiom the pop-up's Letter link uses
// (rabenConsentModal.letterUrl per locale). RG 28.08 W2: the rule itself
// lives in statementFormat.ts, shared with the on-screen Target Statement, so
// the screen and the file can never pick different languages.
export function resolvePdfLanguage(
  uiLanguage: string
): SupplierCommitmentPdfLanguage {
  return resolveStatementLanguage(uiLanguage);
}

export interface SupplierCommitmentPdfProps {
  snapshot: RabenConsentSnapshotSchema;
  language: string;
}

// The German letter is shorter (no per-scenario appendix pages before the
// signature block), so its fillable fields sit on page 2 rather than 3.
export const LETTER_PAGE: Record<SupplierCommitmentPdfLanguage, number> = {
  pl: 3,
  en: 3,
  de: 2,
};

export const LETTER_SOURCE_URL: Record<SupplierCommitmentPdfLanguage, string> =
  {
    pl: '/assets/raben/supplier-commitment-letter-pl.pdf',
    en: '/assets/raben/supplier-commitment-letter-en.pdf',
    de: '/assets/raben/supplier-commitment-letter-de.pdf',
  };

// Word-for-word identical on both the Commitment Letter (this file) and
// the target statement (TargetPdfDocument.ts imports these) -- verified
// against both source PDFs' text layers. Kept in one place so both
// documents' anchors stay in sync if Raben ever revises the wording.
export const COMMON_FIELD_LABELS: Record<
  SupplierCommitmentPdfLanguage,
  { companyName: string; taxId: string; date: string; signature: string }
> = {
  pl: {
    companyName: 'Nazwa firmy:',
    taxId: 'NIP:',
    date: 'Data:',
    signature: 'Podpis:',
  },
  en: {
    companyName: 'Company name:',
    taxId: 'Tax identification number:',
    date: 'Date:',
    signature: 'Signature:',
  },
  // Verified against both German source PDFs' text layers (join('') of the
  // glyph runs, the same way pdfAnchors matches): the letter's page 2 and
  // the statement's page 1 carry these exact labels. "Datum:" is safe even
  // though page 2 also says "ab dem Datum der Einreichung" -- that mention
  // has no colon.
  de: {
    companyName: 'Firmenname:',
    taxId: 'Steuer-Identifikationsnummer:',
    date: 'Datum:',
    signature: 'Unterschrift:',
  },
};

export interface CommonFieldValues {
  companyName: string;
  taxId: string;
  date: string;
  signature: string;
}

// Both documents fill in the same 4 fields (company name / tax id / date /
// signature) right after the same labels -- shared here so TargetPdfDocument
// doesn't repeat the phrase-to-anchor wiring.
export function buildCommonFieldDraws(
  pdfLanguage: SupplierCommitmentPdfLanguage,
  values: CommonFieldValues
): Array<{ spec: AnchorSpec; value: string }> {
  const labels = COMMON_FIELD_LABELS[pdfLanguage];
  return [
    {
      spec: { phrase: labels.companyName, mode: 'after' },
      value: values.companyName,
    },
    { spec: { phrase: labels.taxId, mode: 'after' }, value: values.taxId },
    { spec: { phrase: labels.date, mode: 'after' }, value: values.date },
    {
      spec: { phrase: labels.signature, mode: 'after' },
      value: values.signature,
    },
  ];
}

// One date per document language: 27.08.2026 (pl/de) / 27/08/2026 (en).
// Shared with TargetPdfDocument so both documents agree, and with the
// on-screen Statement through statementFormat.ts (RG 28.08 W2: one
// implementation, so the screen and the file show the same date).
export function formatPdfDate(
  pdfLanguage: SupplierCommitmentPdfLanguage,
  isoDate: string
): string {
  return formatStatementDate(pdfLanguage, isoDate);
}

export function buildSupplierCommitmentFilename(
  snapshot: RabenConsentSnapshotSchema
): string {
  // Deterministic on purpose (EC-4): the same snapshot always produces the
  // same filename -- the ACCEPTANCE date, not "today"'s date, so a later
  // regeneration downloads a file with the same name every time.
  const day = snapshot.accepted_on.slice(0, 10);
  return `Supplier_Commitment_${snapshot.vat_prefixed}_${day}.pdf`;
}

// Goal 3: "auto-download fires right after Accept (from popup or tab)".
// Both RabenConsentModal's Accept button and this tab's Accept/redownload
// buttons call this same function from a mutation's onSuccess / click
// handler (T3: "popup Accept path reuses the same download hook").
export async function downloadSupplierCommitmentPdf(
  props: SupplierCommitmentPdfProps
): Promise<void> {
  const { snapshot, language } = props;
  const pdfLanguage = resolvePdfLanguage(language);

  const formattedDate = formatPdfDate(pdfLanguage, snapshot.accepted_on);

  const draws = buildCommonFieldDraws(pdfLanguage, {
    companyName: snapshot.company_name,
    taxId: snapshot.vat_prefixed,
    date: formattedDate,
    signature: snapshot.company_name,
  });

  const bytes = await stampPdf({
    sourceUrl: LETTER_SOURCE_URL[pdfLanguage],
    pageNumber: LETTER_PAGE[pdfLanguage],
    draws,
    fontUrl: PoppinsRegularUrl,
  });

  downloadPdfBytes(bytes, buildSupplierCommitmentFilename(snapshot));
}
